import { mcn } from "@/utils/mergeClassNames";
import { Disclosure, Tab } from "@headlessui/react";
import { useState } from "react";
import { Markdown } from "../Markdown";
import { Section, SectionContainer, SectionProps } from "./Sections";
import { IconActionChevronDown } from "@/components/Icons";

type Tab = {
  title: string;
  markdownContent: string | undefined | null;
  sectionProps?: SectionProps;
};

type Props = {
  sectionId?: string;
  tabs: Tab[];
  defaultTabIndex?: number;
};

export const InformationTextTabSection = (props: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(
    props.defaultTabIndex ?? 0
  );
  const tab = props.tabs[selectedIndex];
  return (
    <>
      <Section
        className="tablet-sm:fill-screen-height"
        {...tab.sectionProps}
        id={props.sectionId}
      >
        <SectionContainer>
          <h2 className="text-style-headline2 tablet:text-center mb-xl">
            Information
          </h2>
          <div className="tablet-sm:hidden">
            {props.tabs.map((tab, index) => (
              <div className="mt-md" key={index}>
                <Disclosure key={index} defaultOpen={index === 0}>
                  {({ open }) => (
                    <>
                      {index !== 0 && (
                        <span className="flex w-full h-[1px] bg-blue mb-md" />
                      )}
                      <Disclosure.Button
                        className={mcn(
                          "flex justify-between items-center w-full"
                        )}
                      >
                        <h3
                          className={mcn(
                            "text-style-primary",
                            open && "font-bold"
                          )}
                        >
                          {tab.title}
                        </h3>
                        <IconActionChevronDown
                          className={mcn("w-lg h-lg", open && "rotate-180")}
                        />
                      </Disclosure.Button>

                      <Disclosure.Panel>
                        <Markdown
                          className="columns-1 mt-sm tablet-lg:columns-2 gap-default"
                          content={tab.markdownContent ?? ""}
                        />
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            ))}
          </div>
          <div className="hidden tablet-sm:block">
            <Tab.Group
              selectedIndex={selectedIndex}
              onChange={setSelectedIndex}
            >
              <Tab.List className="space-x-lg tablet:text-center py-xs mb-xl tablet:mb-2xl whitespace-nowrap overflow-x-auto">
                {props.tabs.map((tab) => (
                  <Tab
                    key={tab.title ?? ""}
                    className={({ selected }) =>
                      mcn(
                        "text-style-primary",
                        "focus-visible:default-focus focus-visible:rounded",
                        "border-b-4",
                        "hover:border-red-100",
                        selected && "font-bold",
                        selected ? " border-red-100 " : "border-[transparent]"
                      )
                    }
                  >
                    {tab.title}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                {props.tabs.map((tab, index) => (
                  <Tab.Panel key={index} className="focus:outline-none">
                    <Markdown
                      className="columns-1 tablet-lg:columns-2 gap-default"
                      content={tab.markdownContent ?? ""}
                    />
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </SectionContainer>
      </Section>
    </>
  );
};
